import React from 'react';
import styled from 'styled-components/macro';
import { Spacer, Icon, Text } from 'components/atoms';
import { applyStyleModifiers } from 'styled-components-modifiers';
import PropTypes from 'prop-types';
import { iconType, modifiersType } from 'types';
import { darken } from 'polished';
import { useRouterContext } from 'hooks';

const TAB_MODIFIERS = {
  active: ({ theme }) => `
    background-color: ${theme.colors.bg};
    cursor: default !important;

    &:hover {
      background-color: ${theme.colors.bg};
      cursor: default !important;
    }

    &:focus {
      outline: 0;
      background-color: ${darken(0.08, theme.colors.bg)};
    }
    &:active {
      background-color: ${theme.colors.bg};
    }
  `,
};

const Tab = ({
  id,
  icon,
  title,
  modifiers,
  onClose,
  isClosable,
  onClick,
  to,
}) => {
  const {
    history,
  } = useRouterContext();

  const handleOnClose = e => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (to) {
      history.push(to);
    }
    onClick();
  };

  return (
    <TabWrapper
      modifiers={modifiers}
      title={title}
      onClick={e => handleClick(e)}
    >
      <TabWrapper.Start>
        <Spacer right="xs2">
          <Icon icon={icon} height={14} />
        </Spacer>
        <Text>{title || id}</Text>
      </TabWrapper.Start>
      <TabWrapper.End>
        {isClosable && (
          <ButtonClose onClick={e => handleOnClose(e)}>
            <Icon icon="close" height={12} />
          </ButtonClose>
        )}
      </TabWrapper.End>
    </TabWrapper>
  );
};

const TabWrapper = styled.a`
  padding: ${props => props.theme.spacing.xs2};
  background-color: ${props => props.theme.colors.bgLighterS};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 0;
  min-width: 125px;

  &:hover {
    background-color: ${props => darken(0.05, props.theme.colors.bg)};
    cursor: pointer;
  }

  &:focus {
    background-color: ${props => darken(0.05, props.theme.colors.bg)};
    cursor: pointer;
    outline: none;
  }

  &:active {
    background-color: ${props => props.theme.colors.bgLighterS};
    cursor: pointer;
    outline: none;
  }

  ${applyStyleModifiers(TAB_MODIFIERS)};
`;

TabWrapper.Start = styled.div`
  display: flex;
  align-items: center;
`;
TabWrapper.End = styled.div``;

const ButtonClose = styled.button`
  border-radius: 50%;
  width: ${props => props.theme.sizes.l};
  background-color: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.bgLighterS};
  }

  &:focus {
    background-color: ${props => darken(0.1, props.theme.colors.bg)};
    outline: none;
  }

  &:active {
    background-color: ${props => darken(0.2, props.theme.colors.bg)};
    outline: none;
  }
`;

Tab.propTypes = {
  id: PropTypes.string.isRequired,
  icon: iconType.isRequired,
  modifiers: modifiersType,
  onClose: PropTypes.func,
  title: PropTypes.string,
  isClosable: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string,
};

Tab.defaultProps = {
  modifiers: [],
  onClose: () => null,
  title: null,
  isClosable: true,
  onClick: () => null,
  to: null,
};

export default Tab;
